import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  CarePlan,
  CarePlanGoal,
  CarePlanIntervention,
  Member,
} from '../types';
import {
  Target,
  Activity,
  Calendar,
  User,
  Sparkles,
  Plus,
  CheckCircle,
  Clock,
} from 'lucide-react';

interface GoalWithInterventions extends CarePlanGoal {
  interventions: CarePlanIntervention[];
}

export default function CarePlanPage() {
  const [member, setMember] = useState<Member | null>(null);
  const [carePlan, setCarePlan] = useState<CarePlan | null>(null);
  const [goals, setGoals] = useState<GoalWithInterventions[]>([]);
  const [loading, setLoading] = useState(true);
  const [cadence, setCadence] = useState({
    frequency: 'weekly',
    dayOfWeek: 'Monday',
    timeOfDay: '10:00 AM',
  });

  useEffect(() => {
    fetchCarePlanData();
  }, []);

  const fetchCarePlanData = async () => {
    try {
      // Fetch Maria Rodriguez
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('*')
        .eq('member_id', 'MEM001')
        .single();

      if (memberError) throw memberError;
      setMember(memberData);

      if (memberData) {
        // Fetch active care plan
        const { data: planData, error: planError } = await supabase
          .from('care_plans')
          .select('*')
          .eq('member_id', memberData.id)
          .eq('status', 'Active')
          .single();

        if (planError && planError.code !== 'PGRST116') throw planError;

        if (planData) {
          setCarePlan(planData);

          // Fetch goals
          const { data: goalsData, error: goalsError } = await supabase
            .from('care_plan_goals')
            .select('*')
            .eq('care_plan_id', planData.id)
            .order('created_at', { ascending: true });

          if (goalsError) throw goalsError;

          // Fetch interventions for each goal
          if (goalsData) {
            const goalsWithInterventions = await Promise.all(
              goalsData.map(async (goal) => {
                const { data: interventionsData, error: interventionsError } =
                  await supabase
                    .from('care_plan_interventions')
                    .select('*')
                    .eq('goal_id', goal.id)
                    .order('created_at', { ascending: true });

                if (interventionsError) throw interventionsError;

                return {
                  ...goal,
                  interventions: interventionsData || [],
                };
              })
            );

            setGoals(goalsWithInterventions);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching care plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Achieved':
      case 'Completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading care plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Care Plan</h1>
          {member && (
            <p className="text-gray-600 mt-1">
              {member.first_name} {member.last_name} ({member.member_id})
            </p>
          )}
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200 flex items-center">
            <Sparkles className="w-5 h-5 mr-2" />
            Generate care plan draft (AI)
          </button>
          <button className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Explain recommendations
          </button>
        </div>
      </div>

      {/* Care Plan Overview */}
      {carePlan ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {carePlan.title}
            </h2>
            <span
              className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(
                carePlan.status
              )}`}
            >
              {carePlan.status}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-6 text-sm">
            <div>
              <p className="text-gray-500">Start Date</p>
              <p className="font-medium text-gray-900 mt-1">
                {formatDate(carePlan.start_date)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">End Date</p>
              <p className="font-medium text-gray-900 mt-1">
                {formatDate(carePlan.end_date)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Created By</p>
              <p className="font-medium text-gray-900 mt-1">
                {carePlan.created_by || 'N/A'}
              </p>
            </div>
          </div>
          {carePlan.ai_generated && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-purple-600 flex items-center">
                <Sparkles className="w-4 h-4 mr-2" />
                This care plan was AI-generated based on member's clinical
                profile and evidence-based guidelines
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-6">
          <p className="text-amber-800">
            No active care plan found for this member.
          </p>
        </div>
      )}

      {/* Goals and Interventions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Goals & Interventions
          </h2>
          <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center">
            <Plus className="w-5 h-5 mr-2" />
            Add Goal
          </button>
        </div>

        {goals.length > 0 ? (
          goals.map((goal) => (
            <div
              key={goal.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              {/* Goal Header */}
              <div className="p-6 bg-gradient-to-r from-purple-50 to-purple-50 border-b border-purple-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <Target className="w-6 h-6 text-purple-600 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 text-lg">
                        {goal.goal_text}
                      </h3>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Target: {formatDate(goal.target_date)}
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                            goal.status
                          )}`}
                        >
                          {goal.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-700 font-medium">
                      Progress
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {goal.progress_percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${goal.progress_percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Interventions */}
              <div className="p-6">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                  Interventions
                </h4>
                <div className="space-y-3">
                  {goal.interventions.length > 0 ? (
                    goal.interventions.map((intervention) => (
                      <div
                        key={intervention.id}
                        className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors"
                      >
                        <div className="flex-shrink-0">
                          {intervention.status === 'Completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 font-medium">
                            {intervention.intervention_text}
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
                            {intervention.frequency && (
                              <span className="flex items-center">
                                <Activity className="w-3 h-3 mr-1" />
                                {intervention.frequency}
                              </span>
                            )}
                            {intervention.due_date && (
                              <span className="flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                Due: {formatDate(intervention.due_date)}
                              </span>
                            )}
                            {intervention.assigned_to && (
                              <span className="flex items-center">
                                <User className="w-3 h-3 mr-1" />
                                {intervention.assigned_to}
                              </span>
                            )}
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(
                                intervention.status
                              )}`}
                            >
                              {intervention.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No interventions defined for this goal
                    </p>
                  )}
                </div>
                <button className="mt-4 w-full px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors border border-purple-200 flex items-center justify-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Intervention
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              No goals defined yet. Click "Add Goal" to get started.
            </p>
          </div>
        )}
      </div>

      {/* Cadence Scheduler */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Contact Cadence Scheduler
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequency
            </label>
            <select
              value={cadence.frequency}
              onChange={(e) =>
                setCadence({ ...cadence, frequency: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Day of Week
            </label>
            <select
              value={cadence.dayOfWeek}
              onChange={(e) =>
                setCadence({ ...cadence, dayOfWeek: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time of Day
            </label>
            <select
              value={cadence.timeOfDay}
              onChange={(e) =>
                setCadence({ ...cadence, timeOfDay: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="9:00 AM">9:00 AM</option>
              <option value="10:00 AM">10:00 AM</option>
              <option value="11:00 AM">11:00 AM</option>
              <option value="1:00 PM">1:00 PM</option>
              <option value="2:00 PM">2:00 PM</option>
              <option value="3:00 PM">3:00 PM</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
            Save Cadence Schedule
          </button>
        </div>
      </div>
    </div>
  );
}
